/**
 * useCreateLessonBooking — POST /lesson-bookings. [BUT-843, ours]
 *
 * This hook does NOT take `useCreateBooking`'s `{ onSuccess, onFailure,
 * onError }` option bag. That bag exists because `PaymentDetailsStep` answers
 * a failure with a `butterToast`; the lesson payment step instead renders a
 * persistent `LessonBookingFailure` inside the step, so the call site awaits
 * `mutateAsync` and branches on the envelope directly
 * (docs/conventions/internalapi-envelope.md § "A failure the user reads
 * comes from the envelope, at the call site").
 *
 * `retry: false` — one attempt per gesture. A retried POST is a second
 * booking and a second charge, the same reason `useCreateBooking` carries it.
 *
 * @module lessons/hooks/useCreateLessonBooking
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { appRuntime } from '~/services/appRuntime';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { createLessonBooking } from '../api/createLessonBooking';
import type { CreateLessonBookingRequest } from '../types';

export function useCreateLessonBooking() {
  const queryClient = useQueryClient();
  const { data: club } = useClubQuery();
  const clubId = club?.id;

  return useMutation({
    // Returns the ApiResult UNCHANGED. The call site branches (internalapi-envelope.md).
    mutationFn: (body: CreateLessonBookingRequest) => {
      if (!clubId) throw new Error('clubId is required');
      return createLessonBooking(body, { clubId, tenantHandle: appRuntime.tenantHandle ?? '' });
    },
    onSuccess: (envelope) => {
      // A `success: false` is DATA, not an error — TanStack's error path is for exceptions.
      if (!envelope.success || !clubId) return;
      const { booking } = envelope.payload;
      queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all(clubId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(booking.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.lessons.all(clubId) });
    },
    // One attempt per gesture — a retried POST is a second booking and a second charge.
    retry: false,
  });
}
